import { stringify } from 'querystring';
import React, { type FC } from 'react';
import {
  QUERY_DELIMITER,
  Portal as PortalLinks,
} from '@is24at/react-constants';
import { FlexBox } from '@is24at/react-ui-core';
import {
  type Listing,
  type PsaGroupedByCountryCodeResult,
} from 'server/types/graphql';
import { PsaSearchParamsEnum } from 'utils/enums';
import { getCountryNameWithPreposition } from 'utils/getCountryNameWithPreposition';
import Section from '../Section';
import styles from './PropertyList.scss';

type PropertyListProps = {
  commercialHits: Listing[];
  residentialHits: Listing[];
  vacationHitGroups: PsaGroupedByCountryCodeResult[];
  isLoading?: boolean;
  companyName: string;
  baseUrl: string;
  cwid: string;
};

const buildUrl = (
  path: string,
  cwid: string,
  params?: Record<string, string>,
) =>
  `${path}${QUERY_DELIMITER}${stringify({
    [PsaSearchParamsEnum.accountCwid]: cwid,
    ...params,
  })}`;

const PropertyList: FC<PropertyListProps> = ({
  commercialHits,
  residentialHits,
  vacationHitGroups,
  isLoading,
  companyName,
  baseUrl,
  cwid,
}) => {
  return (
    <FlexBox
      spacing={0}
      direction="column"
      alignItems="normal"
      className={styles.container}
    >
      <Section
        hits={residentialHits}
        title={
          residentialHits?.length > 0
            ? `Neueste Wohnimmobilien von ${companyName}`
            : `${companyName} hat zurzeit keine Wohnimmobilien auf unserer Plattform`
        }
        baseUrl={baseUrl}
        isLoading={isLoading}
        moreBtnProps={{
          url: buildUrl(PortalLinks.RESIDENTIAL_ALL_ROUTE_SEGMENT, cwid),
          label: 'weitere Wohnimmobilien',
        }}
        companyName={companyName}
      />
      <Section
        hits={commercialHits}
        title={
          commercialHits?.length > 0
            ? `Neueste Gewerbeimmobilien von ${companyName}`
            : `${companyName} hat zurzeit keine Gewerbeimmobilien auf unserer Plattform`
        }
        baseUrl={baseUrl}
        isLoading={isLoading}
        moreBtnProps={{
          url: buildUrl(PortalLinks.COMMERCIAL_ALL_ROUTE_SEGMENT, cwid),
          label: 'weitere Gewerbeimmobilien',
        }}
        companyName={companyName}
      />
      {vacationHitGroups?.map((group) => {
        return (
          <Section
            key={group.countryCode}
            hits={group.data?.hits}
            title={`Immobilien ${getCountryNameWithPreposition(
              group.countryCode,
            )}`}
            baseUrl={baseUrl}
            isLoading={isLoading}
            moreBtnProps={{
              url: buildUrl(PortalLinks.ROOT, cwid, {
                [PsaSearchParamsEnum.countryCode]: group.countryCode,
              }),
              label: 'weitere Immobilien',
            }}
            companyName={companyName}
          />
        );
      })}
    </FlexBox>
  );
};

export default PropertyList;
